import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { PGHOktaService } from 'src/app/core/services/pghokta.service';
import { ISelectedContract, LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { ContractStatusType, IContract, IFamilyMember } from 'src/app/model/contract/IContract';
import { AccountService } from '../../../../core/services/account.service';
import { UtilityService } from '../../../../core/services/utility.service';
import { ADPResourceService } from 'digital-core';
import { OktaUserProfile } from 'src/app/model/okta/userClaims';
import { StorageService } from 'src/app/core/services/storage.service';

@Component({
  selector: 'app-dashboard-hero',
  templateUrl: './dashboard-hero.component.html',
  styleUrls: ['./dashboard-hero.component.scss']
})
export class DashboardHeroComponent implements OnInit, OnDestroy {
  @Input() disableAccount: boolean;
  @Input() notifyPhonesPending?: boolean;
  @Input() notifyPhonesFailed?: boolean;

  loggedInUserEmail: string = '';
  elitaContracts: IContract[] = null;
  eprismContracts: any[] = null;
  selectedContract: ISelectedContract = null;
  familyMembers: IFamilyMember[] = null;
  address: string;
  loggedInUserFirstName: string;
  isUserLoggedInFirstTime: boolean = false;
  isPhonePlanSupported: boolean = false;
  isPhonesPendingNotification: boolean = false;
  isPhonesFailedNotification: boolean = false;
  oktaUserProfile: OktaUserProfile;
  private destroy$ = new Subject<boolean>();
  
  constructor(
    private userSettings: LoggedInUserSettings,
    private util: UtilityService,
    private accountService: AccountService,
    private pghOktaService: PGHOktaService,
    private storageService: StorageService,
    private resourceService: ADPResourceService
  ) { }

  async ngOnInit(): Promise<void> {
    const userClaims = await this.pghOktaService.getUser();
    this.isPhonesFailedNotification = this.notifyPhonesFailed;
    this.isPhonesPendingNotification = this.notifyPhonesPending;
    this.isPhonePlanSupported = this.resourceService.isPortalSettingTrue('Global_CTC_IsPhonePlanSupported');
    this.loggedInUserEmail = userClaims.email;
    this.loggedInUserFirstName = this.util.getPascalCase(userClaims?.given_name);
    this.isUserLoggedInFirstTime = await this.accountService.isUserLoggedInFirstTime().toPromise();
    this.oktaUserProfile = this.storageService.getLocal<OktaUserProfile>('okta-user-profile');
    this.userSettings.$contracts
      .pipe(takeUntil(this.destroy$))
      .subscribe(contracts => this.elitaContracts = contracts);

    this.userSettings.$eprismContracts
      .pipe(takeUntil(this.destroy$))
      .subscribe(eprismContracts => this.eprismContracts = eprismContracts);

    this.userSettings.$selectedContract
      .pipe(filter(selectedContract => !!selectedContract))
      .subscribe(selected => {
        if (!selected) {
          return;
        }
        this.selectedContract = selected;
        if (this.selectedContract) {
          if (this.selectedContract.addressLine1) {
            this.selectedContract.addressLine1 = this.util.getPascalCase(this.selectedContract.addressLine1);
          }
          if (this.selectedContract.lastName) {
            this.selectedContract.lastName = this.util.getPascalCase(this.selectedContract.lastName);
          }
          if (this.selectedContract.city && this.selectedContract.state && this.selectedContract.zipCode) {
            this.address = `${this.util.getPascalCase(this.selectedContract.city)}, ${this.selectedContract.state} ${this.selectedContract.zipCode}`;
          }
        }
        this.familyMembers = selected.members;
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  contractChange(event: MatSelectChange) {
    if (this.elitaContracts?.length) {
      const contract = this.elitaContracts.find(c => c.id === event.value);
      this.userSettings.setSelectedContract({
        ...contract,
        isPrimary: this.loggedInUserEmail.toLowerCase() === contract.primaryUserEmail.toLowerCase(),
        isExpired: contract.status === ContractStatusType.Expired,
        isCancelled: contract.status === ContractStatusType.Cancelled,
        isSuspended: contract.subscriberStatusCode === 'S',
        isPTPO: this.userSettings.isPTPO(contract.dealerId),
        title: this.elitaContracts?.length > 1 ? `Pocket Geek Home Plan ${this.elitaContracts.findIndex(c => c.id === event.value) + 1}` : `Pocket Geek Home Plan`,
        isEprismContract: false,
        oktaPrimaryPhone: this.oktaUserProfile.primaryPhone,
      });
    }
    else if (this.eprismContracts?.length) {
      const contract = this.eprismContracts.find(x => x.policyIdentifier.mobileNumber == this.oktaUserProfile.primaryPhone);
      const selectedContract = this.userSettings.initializeEprismContract(contract);
      this.userSettings.setSelectedContract(selectedContract);
    }    
  }
}
